import React, {
    Fragment,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from 'react';
import { connect, useSelector } from 'react-redux';
import { Scrollbars } from 'react-custom-scrollbars';

import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';
import Button from '@material-ui/core/Button';

import * as ApiTimeClock from '../../../api/ApiTimeClock';
import { getDepId } from '../../../utilities/Utils';
import { checkResStatusService } from '../../../utilities/ResServiceUtils';
import {
    getFirstDateOfCurentMount,
    getLastDateOfCurentMount,
    convertDateDisplay,
    formatDate,
    formatTime,
} from '../../../utilities/DatetimeUtils';
import { PERMISSION_ORG } from '../../../constants/APDConstants';
import * as AlertMSGConstants from '../../../constants/AlertMSGConstants';
import {
    getLutExclusiveOrgLeave,
    getCustomLutOrgLeave,
} from '../../../api/ApiOrgLeave';

import Loading from '../../../control/loading/Loading';
import UseLoadingState from '../../../control/loading/UseLoadingState';
import AlertMessage from '../../../control/alert/AlertMessage';
import UseAlertMessageState from '../../../control/alert/UseAlertMessageState';
import DataTable from '../../../control/table/DataTable';

import GetAppIcon from '@material-ui/icons/GetApp';

import SearchForm from './SearchForm';
import { getFormValues } from 'redux-form';

const columns = [
    {
        title: 'ลำดับ',
        data: 'index',
        type: 'number',
        width: '3%',
    },
    {
        title: 'รหัสพนักงาน',
        data: 'empId',
        sort: true,
        width: '6%',
    },
    {
        title: 'ชื่อ-นามสกุล',
        data: 'fullName',
        sort: true,
        width: '15%',
        align: 'left',
    },
    {
        title: 'ฝ่าย',
        data: 'depShortName',
        sort: true,
        width: '8%',
        align: 'center',
    },
    {
        title: 'กอง',
        data: 'divName',
        sort: true,
        width: '12%',
        align: 'left',
    },
    {
        title: 'แผนก',
        data: 'secName',
        sort: true,
        width: '12%',
        align: 'left',
    },
    {
        title: 'วันที่',
        data: 'workingDate',
        type: 'date',
        sort: true,
        width: '8%',
    },
    {
        title: 'เวลา',
        data: 'workingTime',
        sort: true,
        width: '5%',
    },
    {
        title: 'สถานะการส่ง SAP',
        data: 'sapSendingStatusName',
        sort: true,
        width: '8%',
    },
    {
        title: 'ประเภท',
        data: 'attendanceTypeName',
        sort: true,
        width: '5%',
    },
    {
        title: 'ประเภทการบันทึกเวลา',
        data: 'clockingTypeName',
        sort: true,
        width: '11%',
    },
];

const useStyles = makeStyles((theme) => ({
    root: {
        width: '100%',
        height: '100%',
    },
    rootPaper: {
        minHeight: 'calc(100% - 48px)',
        margin: '24px',
        [theme.breakpoints.down('sm')]: {
            width: 'calc(98% - 48px)',
        },
        ...theme.paperPadding,
    },
    title: {
        fontSize: '33px',
    },
    form: {
        marginTop: theme.spacing(2),
    },
    divider: {
        width: '100%',
        margin: '24px 0px',
    },
    result: {
        alignSelf: 'flex-end',
        textAlign: 'end',
    },
    buttonExportAll: {
        height: '40px',
        marginBottom: '5px',
        padding: '0px',
        marginLeft: '10px',
        paddingRight: '10px',
        paddingLeft: '10px',
        backgroundColor: '#016f01 !important',
        color: '#FFFFFF',
    },
}));

const defaultValues = {
    orgId: null,
    keyword: '',
    startDate: getFirstDateOfCurentMount(
        new Date(new Date().setMonth(new Date().getMonth()))
    ),
    endDate: getLastDateOfCurentMount(
        new Date(new Date().setMonth(new Date().getMonth()))
    ),
    clockingTypeId: null,
};

const formId = 'TimeClockSapHistorySearchForm';

const SapHistory = ({ authenRdc }) => {
    const values = useSelector((state) => getFormValues(formId)(state));

    const classes = useStyles();
    const alertMessageState = UseAlertMessageState();
    const loadingState = UseLoadingState();

    const [lookups, setLookups] = useState({
        clockingType: [
            {
                id: 1,
                label: 'บันทึกเวลาปกติ',
            },
            {
                id: 2,
                label: 'คำขอเพิ่มบันทึกเวลา',
            },
        ],
        organization: [],
    });
    const [tableConfig, setTableConfig] = useState({
        currentPage: 1,
        pageSize: 10,
        sortField: null,
        sortOrder: null,
        total: 0,
    });
    const [filters, setFilters] = useState({ ...defaultValues });
    const [results, setResults] = useState([]);

    const depId = useMemo(() => getDepId(authenRdc.profile), [authenRdc]);

    const getLookups = useCallback(async () => {
        loadingState.showLoading();
        try {
            const response = await (authenRdc.profile.permissionOrgId ===
            PERMISSION_ORG.EXCLUSIVE_ORG
                ? getLutExclusiveOrgLeave(depId)
                : getCustomLutOrgLeave());
            loadingState.hideLoading();
            if (response.data) {
                setLookups({
                    ...lookups,
                    organization: response.data,
                });
            } else {
                alertMessageState.showAlert({
                    altMsg:
                        checkResStatusService(response) ||
                        AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        } catch (error) {
            loadingState.hideLoading();
            alertMessageState.showAlert({
                altMsg: AlertMSGConstants.SYSTEM_ERROR,
            });
        }
    }, [authenRdc, loadingState, alertMessageState, depId, lookups]);

    const fetchData = useCallback(
        async (newTableConfig = {}, newFilters = {}) => {
            loadingState.showLoading();
            setFilters({
                ...filters,
                ...newFilters,
            });
            const permissionOrgId = authenRdc.profile.permissionOrgId;
            const parameters = {
                ...tableConfig,
                ...newTableConfig,
                ...filters,
                ...newFilters,
                permissionOrgId: permissionOrgId,
                depId: [
                    PERMISSION_ORG.PERSONAL_ORG,
                    PERMISSION_ORG.EXCLUSIVE_ORG,
                ].includes(permissionOrgId)
                    ? authenRdc.profile.depId
                    : null,
            };
            delete parameters.total;
            try {
                const response = await ApiTimeClock.fetchSapHistory(parameters);

                loadingState.hideLoading();
                if (response.data.items) {
                    const startIndex =
                        parameters.pageSize * (parameters.currentPage - 1) + 1;
                    setResults(
                        response.data.items.map((item, index) => ({
                            ...item,
                            index: startIndex + index,
                            workingDate: item.workingDate
                                ? convertDateDisplay(
                                      formatDate(new Date(item.workingDate))
                                  )
                                : '-',
                            workingTime: item.workingTimeDisplay,
                            depShortName: item.depShortName
                                ? item.depShortName
                                : '-',
                        }))
                    );
                    setTableConfig((prev) => ({
                        ...prev,
                        ...newTableConfig,
                        total: response.data.total,
                    }));
                } else {
                    alertMessageState.showAlert({
                        altMsg:
                            checkResStatusService(response) ||
                            AlertMSGConstants.SYSTEM_ERROR,
                    });
                }
            } catch (error) {
                loadingState.hideLoading();
                alertMessageState.showAlert({
                    altMsg: AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [
            alertMessageState,
            authenRdc.profile.depId,
            authenRdc.profile.permissionOrgId,
            filters,
            loadingState,
            tableConfig,
        ]
    );

    const exportData = useCallback(
        async (newTableConfig = {}, newFilters = {}) => {
            loadingState.showLoading();
            setFilters({
                ...filters,
                ...newFilters,
            });
            const permissionOrgId = authenRdc.profile.permissionOrgId;
            const parameters = {
                ...tableConfig,
                ...newTableConfig,
                ...filters,
                ...newFilters,
                permissionOrgId: permissionOrgId,
                depId: [
                    PERMISSION_ORG.PERSONAL_ORG,
                    PERMISSION_ORG.EXCLUSIVE_ORG,
                ].includes(permissionOrgId)
                    ? authenRdc.profile.depId
                    : null,
            };
            delete parameters.total;
            delete parameters.pageSize;
            delete parameters.currentPage;
            try {
                const response = await ApiTimeClock.exportSapHistory(
                    parameters
                );
                loadingState.hideLoading();
                if (response) {
                    const url = window.URL.createObjectURL(response);
                    const link = document.createElement('a');
                    link.href = url;

                    link.setAttribute(
                        'download',
                        'ประวัติการนำข้อมูลเข้า_SAP.xlsx'
                    );
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(url);
                } else {
                    alertMessageState.showAlert({
                        altMsg:
                            // checkResStatusService(response) ||
                            AlertMSGConstants.SYSTEM_ERROR,
                    });
                }
            } catch (error) {
                loadingState.hideLoading();
                alertMessageState.showAlert({
                    altMsg: AlertMSGConstants.SYSTEM_ERROR,
                });
            }
        },
        [
            alertMessageState,
            authenRdc.profile.depId,
            authenRdc.profile.permissionOrgId,
            filters,
            loadingState,
            tableConfig,
        ]
    );

    useEffect(() => {
        getLookups();
        fetchData({ currentPage: 1 });
    }, []);

    const handleClickExport = useCallback(() => {
        exportData({ ...values });
    }, [exportData, values]);

    const handleSubmitSearchForm = useCallback(
        (values) => {
            fetchData({ currentPage: 1 }, { ...values });
        },
        [fetchData]
    );

    const handleSortChange = useCallback(
        (sortField, sortOrder) => {
            fetchData({
                currentPage: 1,
                sortField,
                sortOrder,
            });
        },
        [fetchData]
    );

    const handlePageChange = useCallback(
        (currentPage) => {
            fetchData({ currentPage });
        },
        [fetchData]
    );

    return (
        <Fragment>
            <Scrollbars universal={true}>
                <Paper className={classes.rootPaper}>
                    <Grid container justify="center">
                        <Grid item xs={12} className={classes.form}>
                            <SearchForm
                                defaultValues={defaultValues}
                                lookups={lookups}
                                showOrganization={
                                    authenRdc.profile.permissionOrgId !==
                                    PERMISSION_ORG.PERSONAL_ORG
                                }
                                onSubmit={handleSubmitSearchForm}
                            />
                        </Grid>
                        <Grid item xs={12} className={classes.divider}>
                            <Divider />
                        </Grid>
                        <Grid item xs={12} md={6} className={classes.title}>
                            ประวัติการนำข้อมูลเข้า SAP
                            <Button
                                variant="contained"
                                className={classes.buttonExportAll}
                                startIcon={<GetAppIcon />}
                                onClick={handleClickExport}
                            >
                                ออกรายงาน EXCEL
                            </Button>
                        </Grid>
                        <Grid item xs={12} md={6} className={classes.result}>
                            พบผลลัพธ์ทั้งหมด {tableConfig.total} รายการ
                        </Grid>
                        <Grid item xs={12}>
                            <DataTable
                                column={columns}
                                rowPerPage={tableConfig.pageSize}
                                notSort={true}
                                page={tableConfig.currentPage}
                                data={results}
                                total={tableConfig.total}
                                sortColumn={tableConfig.sortField}
                                sortType={tableConfig.sortOrder}
                                handleSortExternal={handleSortChange}
                                handlePageChange={handlePageChange}
                                minWidth="2000px"
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Scrollbars>
            <AlertMessage {...alertMessageState.altProps} />
            <Loading isShowLoading={loadingState.isShowLoading} />
        </Fragment>
    );
};

export default connect((state) => ({
    authenRdc: state.authen,
}))(SapHistory);
